import React, { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Col, Divider, Row, Space, Spin } from 'antd'
import { groupBy, isEmpty } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import {
  HistoryLink,
  NodeBreadcrumbs,
} from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import { EVENT_METADATA_TYPES, METADATA_TYPES } from '../../../common/constants'
import { showErrorNotification, transformTree } from '../../../common/helpers'
import {
  useDietsQuery,
  useDocumentTitle,
  useIngredientsQuery,
  useLanguagesQuery,
  useUpdatedIngredientSubscription,
} from '../../../common/hooks'
import { flattenTreeStructure } from '../../../common/utils'

import { renderIngredientEvent } from '../../helpers'
import { useIngredientQuery } from '../../hooks'
import {
  IngredientAlternativeIngredientsDetailsForm,
  IngredientComponentDetailsForm,
  IngredientExtraDetailsForm,
  IngredientImageDetailsForm,
  IngredientKeywordDetailsForm,
  IngredientNotesForm,
  IngredientOverridesIngredientsDetailsForm,
  IngredientSimpleDetailsForm,
} from '../forms'

const IngredientDetailsPage = () => {
  const { id } = useParams()

  const isIngredientPageDirty = useSelector(
    store =>
      store.ingredients.ingredient.isAlternativeIngredientsDirty ||
      store.ingredients.ingredient.isComponentIngredientsDirty ||
      store.ingredients.ingredient.isExtraIngredientsDirty ||
      store.ingredients.ingredient.isKeywordsDirty ||
      store.ingredients.ingredient.isOverridesIngredientsDirty ||
      store.ingredients.ingredient.isSimpleDetailsDirty ||
      store.ingredients.ingredient.isNotesDirty,
  )

  const {
    isLoading: isFetchLoading,
    error: fetchError,
    data,
    refetch,
  } = useIngredientQuery(id)

  useUpdatedIngredientSubscription(
    id,
    isIngredientPageDirty,
    METADATA_TYPES.INGREDIENTS.value,
    refetch,
  )

  useDocumentTitle(`${data?.name || 'Loading Ingredient...'}`)

  const {
    options: ingredientOptions,
    isLoading: isLoadingIngredients,
    error: ingredientsError,
  } = useIngredientsQuery()

  const filters = useMemo(
    () => ({ isAvailableForDishCategorization: true }),
    [],
  )

  const {
    options: dietOptions,
    isLoading: isLoadingDiets,
    error: dietsError,
  } = useDietsQuery(filters)

  const {
    options: languageOptions,
    isLoading: isLoadingLanguages,
    error: languagesError,
  } = useLanguagesQuery()

  const ingredientOptionsWithoutCurrentIngredient = useMemo(
    () =>
      transformTree(ingredientOptions, node =>
        node.value === id ? { ...node, disabled: true } : node,
      ),
    [ingredientOptions, id],
  )

  const flattenedIngredientOptions = useMemo(
    () => flattenTreeStructure(ingredientOptionsWithoutCurrentIngredient),
    [ingredientOptionsWithoutCurrentIngredient],
  )

  const groupedFlatTreeDataByValue = useMemo(
    () => groupBy(flattenedIngredientOptions, 'value'),
    [flattenedIngredientOptions],
  )

  useEffect(() => {
    if (languagesError || ingredientsError || dietsError || fetchError) {
      showErrorNotification({
        title: 'Ingredient details error',
        description:
          languagesError || ingredientsError || dietsError || fetchError,
      })
    }
  }, [dietsError, ingredientsError, fetchError, languagesError])

  return isFetchLoading ||
    isEmpty(data) ||
    isLoadingDiets ||
    isLoadingIngredients ||
    isLoadingLanguages ? (
    <Spin />
  ) : (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <NodeBreadcrumbs
              metadataType={METADATA_TYPES.INGREDIENTS}
              idPath={data.node.idPath}
              namePath={data.node.namePath}
            />
          </Col>

          <Space>
            <UpdateDataText
              updatedAt={data.updatedAt}
              updatedBy={data.updatedBy?.name}
            />

            <HistoryLink
              renderEvent={renderIngredientEvent}
              entityId={id}
              eventMetadataTypes={[EVENT_METADATA_TYPES.INGREDIENT]}
            />
          </Space>
        </Row>
      </StyledPageSubTitle>
      <IngredientSimpleDetailsForm
        ingredient={data}
        maxPosition={data.maxPosition}
      />
      <Divider />
      <IngredientImageDetailsForm ingredient={data} />
      <Divider />
      {!isEmpty(ingredientOptionsWithoutCurrentIngredient) && (
        <>
          <IngredientComponentDetailsForm
            ingredientId={data.id}
            componentIngredients={data.componentIngredients}
            isWholeFood={data.isWholeFood}
            isVague={data.isVague}
            ingredientOptions={ingredientOptionsWithoutCurrentIngredient}
            groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
          />

          <Divider />
          {!isEmpty(languageOptions) && (
            <IngredientKeywordDetailsForm
              ingredientId={data.id}
              ingredientOptions={ingredientOptionsWithoutCurrentIngredient}
              languageOptions={languageOptions}
            />
          )}
          <Divider />
          <IngredientExtraDetailsForm
            ingredientId={data.id}
            phrases={data.phrases}
            extraIngredients={data.extraIngredients}
            ingredientOptions={ingredientOptionsWithoutCurrentIngredient}
            groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
            flatTreeData={flattenedIngredientOptions}
          />

          <Divider />

          <IngredientOverridesIngredientsDetailsForm
            ingredientId={data.id}
            overridesIngredients={data.overridesIngredients}
            ingredientOptions={ingredientOptionsWithoutCurrentIngredient}
            groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
            flatTreeData={flattenedIngredientOptions}
          />

          <Divider />

          {!isEmpty(dietOptions) && (
            <IngredientAlternativeIngredientsDetailsForm
              ingredientId={data.id}
              alternativeIngredients={data.alternativeIngredients}
              ingredientOptions={ingredientOptionsWithoutCurrentIngredient}
              dietOptions={dietOptions}
              groupedFlatTreeDataByValue={groupedFlatTreeDataByValue}
              flatTreeData={flattenedIngredientOptions}
            />
          )}
        </>
      )}
      <Divider />
      <IngredientNotesForm id={data.id} notes={data.notes} />
    </>
  )
}

export default IngredientDetailsPage
